import { apiUrl, authHeaders } from '@/lib/shell';
import { namespaceCopyFor } from '@/i18n/ns';
import companyErrors from '@/i18n/ns/company-errors';

export interface Company {
  id: string;
  name: string;
  website: string | null;
  market: string | null;
  language: string | null;
  goal: string | null;
  revision: number;
  completedAt: string | null;
}
export interface CompanyContext {
  company: Company | null;
  membership: {
    userId: string;
    organizationId: string;
    role: 'owner' | 'admin' | 'member';
    canPublish?: boolean | null;
    permissions?: {
      privateWork: boolean;
      publishCompany: boolean;
      manageMembers: boolean;
      /** Owner or admin; an admin invites members only (collaboration v1). */
      inviteMembers?: boolean;
      manageConnections: boolean;
      manageBilling: boolean;
      /** BUILD 2: the company voice switches (owner only). */
      manageVoiceSignatures: boolean;
      /** BUILD 2: the company speech policy (owner or admin). */
      manageSpeechPolicy: boolean;
      makeCompanyWorkPrivate?: boolean;
    };
  } | null;
  onboarding: { required: boolean; completedAt: string | null };
}

export class CompanyRequestError extends Error {
  constructor(
    public status: number,
    public code: string,
    /** The item the server refused, when its answer names one (e.g. an import path). */
    public path: string | null = null,
    /** A code's structural `reason`, when the answer carries one (BUILD 2 C2-14). */
    public reason: string | null = null,
    /** Numbers and codes the server attached to the refusal (e.g. a read's time limit). */
    public detail: Record<string, unknown> | null = null,
    /** The server's own sentence for a payment refusal (plan, credits or limit); no other answer's text is shown. */
    publicMessage: string | null = null,
    locale = 'en',
  ) {
    const copy = namespaceCopyFor(companyErrors, locale);
    super(
      (
        {
          recipient_already_assigned: copy.requestPersonAssigned,
          ownership_transfer_required: copy.requestOwnership,
          invalid_invitation_email: copy.requestInviteEmail,
          pipedream_plan_required: copy.requestPipedream,
        } as Record<string, string>
      )[code] ??
        (status === 409
          ? copy.requestChanged
          : status === 403
            ? copy.requestForbidden
            : copy.requestGeneric),
    );
    if (code === 'payment_required' && publicMessage) this.message = publicMessage;
  }
}

export async function companyRequest<T>(
  path: string,
  method = 'GET',
  body?: unknown,
  /** An action bound to its committed session passes that session's fetch (useSessionBoundFetch); absent, the page's own. */
  fetchImpl?: typeof fetch,
  locale = 'en',
): Promise<T> {
  const response = await (fetchImpl ?? fetch)(apiUrl(path), {
    method,
    credentials: 'same-origin',
    headers: {
      ...authHeaders(),
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new CompanyRequestError(
      response.status,
      error.code ?? error.error ?? 'request_failed',
      typeof error.path === 'string' ? error.path : null,
      typeof error.reason === 'string' ? error.reason : null,
      error.detail && typeof error.detail === 'object' && !Array.isArray(error.detail)
        ? error.detail
        : null,
      response.status === 402 && typeof error.message === 'string' ? error.message : null,
      locale,
    );
  }
  return response.status === 204 ? (undefined as T) : response.json();
}

export const fetchCompany = (locale = 'en') =>
  companyRequest<CompanyContext>('/api/v1/company', 'GET', undefined, undefined, locale);
export const completeCompanyWelcome = (locale = 'en') =>
  companyRequest<CompanyContext>('/api/v1/company/welcome', 'POST', {}, undefined, locale);
export const acceptCompanyInvitation = (token: string, locale = 'en') =>
  companyRequest<CompanyContext>(
    '/api/v1/company-invitation/accept',
    'POST',
    { token },
    undefined,
    locale,
  );
export const saveCompany = (company: Company, locale = 'en') =>
  companyRequest<CompanyContext>(
    '/api/v1/company',
    'PATCH',
    {
      name: company.name,
      website: company.website,
      market: company.market,
      language: company.language,
      goal: company.goal,
      revision: company.revision,
    },
    undefined,
    locale,
  );
